import { z } from 'zod';
import { COMMISSIONING_INSTANCE_SCOPES, COMMISSIONING_CONFIGURATOR_TYPE_CODES } from '../types/commissioning';
import { commissioningKernelNodeSchema, commissioningVariableKernelFieldsSchema } from './commissioning-kernel';
import { commissioningInstancePerimeterSchema } from './commissioning-instance-perimeter';
import {
  commissioningVariableComputeConfigSchema, countingConfigSchema, remunerationConfigSchema, templateConfigSchema,
} from './commissioning';
import {
  commissioningAggregateRulesSchema, commissioningRuleVariantSchema,
  commissioningCompetitionClusterDtoSchema, commissioningCompetitionClusterMemberDtoSchema,
} from './commissioning-runtime-v3';

const uuid = z.string().uuid();
const stamp = { created_at: z.string(), updated_at: z.string() };
export const commissioningThresholdFrozenVariableSchema = commissioningVariableKernelFieldsSchema.pick({
  data_type: true, source_kind: true, subject_kind: true, min_phase: true, cardinality: true,
  family_coverage: true, wiring_status: true, missing_policy: true, depends_on: true, unit: true,
}).required().extend({
  code: z.string(), resolver_key: z.string(), is_computed: z.boolean(), is_active: z.boolean(),
  compute_config: commissioningVariableComputeConfigSchema.nullable(),
  version: z.number().int().positive(), valid_from: z.string().nullable(), valid_to: z.string().nullable(),
  is_current_version: z.boolean(),
}).strict();

export const commissioningThresholdFrozenFunctionSchema = z.object({
  id: uuid, version: z.number().int().positive(), condition_tree: commissioningKernelNodeSchema.nullable(),
  status: z.enum(['draft', 'published', 'archived']),
  pinned_bundle: z.object({
    variables: z.record(z.number().int().positive()), functions: z.record(z.number().int().positive()), pinnedAt: z.string(),
  }).strict().nullable(),
}).strict();

export const commissioningFunctionPublicationSnapshotSchema = z.object({
  schemaVersion: z.literal(1),
  pinnedBundle: commissioningThresholdFrozenFunctionSchema.shape.pinned_bundle,
}).strict();
export type CommissioningFunctionPublicationSnapshot = z.infer<typeof commissioningFunctionPublicationSnapshotSchema>;

/** Server-owned content in the EXISTING version variants JSONB, never accepted by an editor payload. */
const thresholdSnapshotBaseSchema = z.object({
  schemaVersion: z.literal(1),
  capturedAt: z.string(),
  effectiveFrom: z.string(),
  instance: z.object({
    id: uuid, tenant_id: uuid, competition_id: uuid, template_id: uuid, name: z.string(),
    scope: z.enum(COMMISSIONING_INSTANCE_SCOPES),
    event_family: z.enum(['sell_out', 'sell_in', 'crm']).nullable(),
    period_start: z.string().nullable(), period_end: z.string().nullable(),
    cap_scope_filter_function_id: uuid.nullable(),
    cap_type: z.enum(['absolute', 'percentage_of_total']).nullable(), cap_value: z.number().nullable(),
    cap_metric: z.enum(['amount_eur', 'valenza', 'count']).nullable(), cap_base_variable_code: z.string().nullable(),
    counting_config: countingConfigSchema.nullable(), remuneration_config: remunerationConfigSchema.nullable(),
    aggregate_rules: commissioningAggregateRulesSchema.nullable(),
    rules_version: z.number().int().nonnegative(), business_driver_id: uuid.nullable(),
    source_driver_ids: z.array(uuid), template_config: templateConfigSchema.nullable(), type_code: z.literal('soglie'),
    /** VP del configuratore (testa della catena), ereditato da cluster e celle driver. */
    value_package_id: uuid.nullable(),
    /** Perimetro prodotto congelato con la versione (assente negli snapshot storici = nessun filtro). */
    perimeter: commissioningInstancePerimeterSchema.nullable().default(null),
  }).strict(),
  assignments: z.array(z.object({
    id: uuid, tenant_id: uuid, instance_id: uuid, competition_cluster_id: uuid,
    rule_variant: commissioningRuleVariantSchema.nullable(),
    is_active: z.boolean(), ...stamp,
  }).strict()),
  drivers: z.array(z.object({
    id: uuid, tenant_id: uuid, assignment_id: uuid, business_driver_id: uuid, tgt_standard: z.number().nullable(),
    tgt_unit: z.string().nullable(), rule_variant: commissioningRuleVariantSchema.nullable(),
    ...stamp,
  }).strict()),
  clusters: z.array(commissioningCompetitionClusterDtoSchema),
  members: z.array(commissioningCompetitionClusterMemberDtoSchema),
  targets: z.array(z.object({
    id: uuid, assignment_driver_id: uuid, member_id: uuid, tgt_value: z.number(),
  }).strict()),
  /** Legal owner of the STORE in this version; never the fiscal company on a sale. */
  storeCompanies: z.array(z.object({ storeId: uuid, companyId: uuid }).strict()).optional(),
  referenceLists: z.array(z.object({
    id: uuid, name: z.string(), code: z.string(), values: z.array(z.string()),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  }).strict()).optional(),
  variables: z.array(commissioningThresholdFrozenVariableSchema),
  functions: z.array(commissioningThresholdFrozenFunctionSchema),
  functionHistory: z.array(z.object({
    function_id: uuid, version: z.number().int().positive(), condition_tree: commissioningKernelNodeSchema,
    publication_snapshot: commissioningFunctionPublicationSnapshotSchema.nullable().optional(),
  }).strict()),
}).strict();

/** Only the common economic contract exists: every member carries its economic
 * subject and the classification axis may be a configurator. The marker is a
 * stored invariant, not a discriminator. */
const commonThresholdSnapshotSchema = thresholdSnapshotBaseSchema.extend({
  classificationContractVersion: z.literal(2),
  // Reuse the existing applied-configuration photograph for every common
  // recipe. This does not confer Soglie/band capabilities on another type.
  instance: thresholdSnapshotBaseSchema.shape.instance.extend({
    type_code: z.enum(COMMISSIONING_CONFIGURATOR_TYPE_CODES),
  }),
});
/** The publication planner normalizes these sections, not an unvalidated snapshot. */
export const commissioningThresholdLayoutSchemas = {
  instance: commonThresholdSnapshotSchema.shape.instance,
  assignments: thresholdSnapshotBaseSchema.shape.assignments,
  drivers: thresholdSnapshotBaseSchema.shape.drivers,
  targets: thresholdSnapshotBaseSchema.shape.targets,
};
export const commissioningThresholdSnapshotSchema = commonThresholdSnapshotSchema.superRefine((value, ctx) => {
  const issue = (path: Array<string | number>, message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
  const unique = (keys: string[], path: string) => {
    if (new Set(keys).size !== keys.length) issue([path], 'Identità duplicate nella fotografia');
  };
  unique(value.assignments.map((row) => row.id), 'assignments');
  unique(value.drivers.map((row) => row.id), 'drivers');
  unique(value.clusters.map((row) => row.id), 'clusters');
  unique(value.members.map((row) => row.id), 'members');
  unique(value.members.map((row) => `${row.economic_subject_id}:${row.configurator_instance_id}:${row.business_driver_id}`), 'members');
  unique(value.targets.map((row) => `${row.assignment_driver_id}:${row.member_id}`), 'targets');
  unique(value.variables.map((row) => `${row.code}:${row.version}`), 'variables');
  unique(value.functions.map((row) => row.id), 'functions');
  unique(value.functionHistory.map((row) => `${row.function_id}:${row.version}`), 'functionHistory');
  unique((value.storeCompanies ?? []).map((row) => row.storeId), 'storeCompanies');
  unique((value.referenceLists ?? []).map((row) => row.id), 'referenceLists');
  const clusters = new Map(value.clusters.map((row) => [row.id, row]));
  const assignments = new Map(value.assignments.map((row) => [row.id, row]));
  const drivers = new Map(value.drivers.map((row) => [row.id, row]));
  const members = new Map(value.members.map((row) => [row.id, row]));
  value.clusters.forEach((row, index) => {
    if (row.tenant_id !== value.instance.tenant_id || row.competition_id !== value.instance.competition_id) {
      issue(['clusters', index], 'Cluster fuori dal perimetro della fotografia');
    }
    if (row.business_driver_id !== null && row.configurator_instance_id !== null) {
      issue(['clusters', index], 'Asse di classificazione non coerente con il contratto');
    }
  });
  value.assignments.forEach((row, index) => {
    if (row.tenant_id !== value.instance.tenant_id || row.instance_id !== value.instance.id
      || !clusters.has(row.competition_cluster_id)) issue(['assignments', index], 'Assegnazione fuori dal perimetro della fotografia');
    const cluster = clusters.get(row.competition_cluster_id);
    if (cluster
      && (cluster.configurator_instance_id !== null && cluster.configurator_instance_id !== value.instance.id
        || cluster.business_driver_id !== null && cluster.business_driver_id !== value.instance.business_driver_id)) {
      issue(['assignments', index], 'La classificazione appartiene a un altro configuratore o driver');
    }
  });
  value.drivers.forEach((row, index) => {
    if (row.tenant_id !== value.instance.tenant_id || !assignments.has(row.assignment_id)) {
      issue(['drivers', index], 'Driver senza assegnazione nella fotografia');
    }
  });
  value.members.forEach((row, index) => {
    const cluster = clusters.get(row.competition_cluster_id);
    if (row.tenant_id !== value.instance.tenant_id || row.competition_id !== value.instance.competition_id
      || !cluster || cluster.business_driver_id !== row.business_driver_id
      || cluster.configurator_instance_id !== row.configurator_instance_id) {
      issue(['members', index], 'Membership fuori dal perimetro o dall’asse della fotografia');
    }
    const identities = [row.store_id, row.company_id, row.user_id].filter((id) => id !== null).length;
    const collective = identities === 0 && row.economic_subject_id !== null && row.source_member_id === null;
    if (!collective && (identities !== 1 || (cluster?.entity_type === 'pdv' && row.store_id === null)
      || (cluster?.entity_type === 'rs' && row.company_id === null)
      || (cluster?.entity_type === 'risorsa' && row.user_id === null))) {
      issue(['members', index], 'Identità del soggetto non coerente con il cluster');
    }
    if (row.economic_subject_id === null) {
      issue(['members', index], 'Proprietà economica non coerente con il contratto');
    }
  });
  value.targets.forEach((row, index) => {
    const driver = drivers.get(row.assignment_driver_id);
    const assignment = driver ? assignments.get(driver.assignment_id) : undefined;
    const member = members.get(row.member_id);
    if (!assignment || !member || member.competition_cluster_id !== assignment.competition_cluster_id) {
      issue(['targets', index], 'Target personale senza membership dell’assegnazione');
    }
  });
  if (value.storeCompanies) {
    const storeIds = new Set(value.members.flatMap((row) => row.store_id ? [row.store_id] : []));
    const mapped = new Set(value.storeCompanies.map((row) => row.storeId));
    if ([...storeIds].some((id) => !mapped.has(id)) || [...mapped].some((id) => !storeIds.has(id))) {
      issue(['storeCompanies'], 'La relazione PDV→ragione sociale deve coprire esattamente i punti vendita della fotografia');
    }
  }
});

export type CommissioningThresholdSnapshot = z.infer<typeof commissioningThresholdSnapshotSchema>;
export type CommissioningThresholdFrozenVariable = z.infer<typeof commissioningThresholdFrozenVariableSchema>;
export type CommissioningThresholdFrozenFunction = z.infer<typeof commissioningThresholdFrozenFunctionSchema>;
